<template>
  <section
    v-if="heroBanners.length > 0"
    id="hero"
  >
    <v-carousel
      v-if="hasCarousel"
      :interval="5000"
      cycle
      hide-delimiter-background
      :show-arrows="false"
      :height="bannerHeight"
    >
      <v-carousel-item
        v-for="item in heroBanners"
        :key="item.id || item.imageUrl"
        :class="{ 'cursor-pointer': hasBannerLink(item) }"
        @click="handleBannerClick(item)"
      >
        <v-img
          :src="resolveBannerImage(item)"
          position="center center"
          :contain="(item.displayMode || 'cover') === 'contain'"
          :cover="(item.displayMode || 'cover') === 'cover'"
          height="100%"
          class="white--text d-flex justify-center align-center"
        >
          <div
            class="hero-content"
            :style="contentStyle(item)"
          >
            <template v-if="item.title">
              <div
                v-for="(line, index) in formatTitleLines(item.title)"
                :key="`title-${item.id || index}-${index}`"
                class="hero-title"
                :style="titleStyle(item)"
              >
                {{ line }}
              </div>
            </template>
            <div
              v-if="getSubtitle(item)"
              class="hero-subtitle"
              :style="subtitleStyle(item)"
            >
              {{ getSubtitle(item) }}
            </div>
            <div
              class="hero-actions"
              :style="actionsStyle(item)"
            >
              <v-btn
                :color="item.btnContactColor || 'primary'"
                class="mr-3"
                rounded
                depressed
                :style="item.btnContactBorderColor ? { borderColor: item.btnContactBorderColor, borderWidth: '2px', borderStyle: 'solid' } : {}"
                @click.stop="handleBannerAction(item)"
              >
                {{ $t('contact.title') }}
              </v-btn>
              <v-btn
                outlined
                rounded
                :color="item.btnDemoColor || 'primary'"
                :style="item.btnDemoBorderColor ? { borderColor: item.btnDemoBorderColor } : {}"
                @click.stop="goToContact"
              >
                {{ $t('home.hero.bookDemo') }}
              </v-btn>
            </div>
          </div>
        </v-img>
      </v-carousel-item>
    </v-carousel>

    <v-img
      v-else
      :src="resolveBannerImage(activeBanner)"
      position="center center"
      :contain="(activeBanner.displayMode || 'cover') === 'contain'"
      :cover="(activeBanner.displayMode || 'cover') === 'cover'"
      :height="bannerHeight"
      class="white--text d-flex justify-center align-center"
      :class="{ 'cursor-pointer': hasBannerLink(activeBanner) }"
      @click="handleBannerClick(activeBanner)"
    >
      <div
        class="hero-content"
        :style="contentStyle(activeBanner)"
      >
        <template v-if="activeBanner.title">
          <div
            v-for="(line, index) in formatTitleLines(activeBanner.title)"
            :key="`title-single-${index}`"
            class="hero-title"
            :style="titleStyle(activeBanner)"
          >
            {{ line }}
          </div>
        </template>
        <div
          v-if="getSubtitle(activeBanner)"
          class="hero-subtitle"
          :style="subtitleStyle(activeBanner)"
        >
          {{ getSubtitle(activeBanner) }}
        </div>
        <div
          class="hero-actions"
          :style="actionsStyle(activeBanner)"
        >
          <v-btn
            :color="activeBanner.btnContactColor || 'primary'"
            class="mr-3"
            rounded
            depressed
            :style="activeBanner.btnContactBorderColor ? { borderColor: activeBanner.btnContactBorderColor, borderWidth: '2px', borderStyle: 'solid' } : {}"
            @click.stop="handleBannerAction(activeBanner)"
          >
            {{ $t('contact.title') }}
          </v-btn>
          <v-btn
            outlined
            rounded
            :color="activeBanner.btnDemoColor || 'primary'"
            :style="activeBanner.btnDemoBorderColor ? { borderColor: activeBanner.btnDemoBorderColor } : {}"
            @click.stop="goToContact"
          >
            {{ $t('home.hero.bookDemo') }}
          </v-btn>
        </div>
      </div>
    </v-img>
  </section>
</template>

<script>
  import { mdiChevronRight } from '@mdi/js'

  export default {
    name: 'Hero',

    provide: {
      theme: { isDark: true },
    },
    data() {
      return {
        icon: {
          right: mdiChevronRight,
        },
        heroBanners: [],
        fallbackImage: require('@/assets/home-bg.jpg'),
        configBannerHeight: null,
      }
    },
    computed: {
      hasCarousel() {
        return this.heroBanners.length >= 2
      },
      bannerHeight() {
        if (this.configBannerHeight) return this.configBannerHeight
        return this.$vuetify.breakpoint.smAndDown ? 220 : 500
      },
      fallbackTitle() {
        return `${this.$t('home.hero.title1')}/n${this.$t('home.hero.title2')}`
      },
      activeBanner() {
        if (this.heroBanners.length > 0) {
          return this.heroBanners[0]
        }
        return {
          title: this.fallbackTitle,
          subtitle: '',
          imageUrl: this.fallbackImage,
          mobileImageUrl: this.fallbackImage,
        }
      },
      // mHeight() {
      //   const height = this.$vuetify.breakpoint.mdAndUp ? '60vh' : '56vh'
      //   return `calc(${height} - ${this.$vuetify.application.top}px)`
      // },
    },
    watch: {
      '$i18n.locale'() {
        this.loadHeroBanners()
      },
    },
    async mounted() {
      await Promise.all([this.loadHeroBanners(), this.loadBannerHeight()])
    },
    methods: {
      async loadHeroBanners() {
        try {
          const banners = await this.$axios({
            method: 'get',
            url: '/public/homepage/banners',
            params: { lang: this.$i18n.locale },
          })
          this.heroBanners = Array.isArray(banners)
            ? banners.filter(item => item && (item.imageUrl || item.mobileImageUrl))
            : []
        } catch (error) {
          this.heroBanners = []
        }
      },
      async loadBannerHeight() {
        try {
          const resp = await this.$axios({
            method: 'get',
            url: '/public/page-hero',
            params: { pages: 'homepage' },
          })
          const item = resp?.homepage
          if (item && item.height && String(item.height).trim()) {
            this.configBannerHeight = String(item.height).trim()
          }
        } catch (e) {
          // 读取失败时使用默认值，无需处理
        }
      },
      normalizeLineBreaks(value) {
        return (value || '').replace(/\\r\\n/g, '\n').replace(/\\n|\/n/g, '\n')
      },
      formatTitleLines(value) {
        const lines = this.normalizeLineBreaks(value)
          .split('\n')
          .map(item => item.trim())
          .filter(Boolean)
        return lines.length > 0 ? lines : [this.$t('home.hero.title1'), this.$t('home.hero.title2')]
      },
      getSubtitle(item) {
        if (!item) {
          return ''
        }
        return item.subtitle || item.description || ''
      },
      resolveBannerImage(item) {
        if (!item) {
          return this.fallbackImage
        }
        const useMobile = this.$vuetify.breakpoint.smAndDown
        const url = useMobile
          ? (item.mobileImageUrl || item.imageUrl || this.fallbackImage)
          : (item.imageUrl || item.mobileImageUrl || this.fallbackImage)
        return url
      },
      titleStyle(item) {
        const style = {}
        if (item.titleColor) style.color = item.titleColor
        const isMobile = this.$vuetify.breakpoint.smAndDown
        const fs = isMobile ? item.mobileTitleFontSize : item.titleFontSize
        if (fs) style.fontSize = fs
        const align = item.contentAlign
        if (align === 'center' || align === 'right') style.textAlign = align
        return style
      },
      subtitleStyle(item) {
        const style = {}
        if (item.subtitleColor) style.color = item.subtitleColor
        const isMobile = this.$vuetify.breakpoint.smAndDown
        const fs = isMobile ? item.mobileSubtitleFontSize : item.subtitleFontSize
        if (fs) style.fontSize = fs
        const align = item.contentAlign
        if (align === 'center' || align === 'right') style.textAlign = align
        return style
      },
      contentStyle(item) {
        const style = {}
        const align = item.contentAlign || 'left'
        const isMobile = this.$vuetify.breakpoint.smAndDown

        if (align === 'center') {
          style.left = '50%'
          style.transform = item.contentOffsetY
            ? `translate(-50%, calc(-50% + ${item.contentOffsetY}))`
            : 'translate(-50%, -50%)'
          style.textAlign = 'center'
          if (item.contentOffsetX) {
            style.marginLeft = item.contentOffsetX
          }
        } else if (align === 'right') {
          style.left = 'auto'
          style.right = item.contentOffsetX || (isMobile ? '6vw' : '10vw')
          style.textAlign = 'right'
          if (item.contentOffsetY) {
            style.transform = `translateY(calc(-50% + ${item.contentOffsetY}))`
          }
        } else {
          // left (default)
          if (item.contentOffsetX) style.left = item.contentOffsetX
          if (item.contentOffsetY) {
            style.transform = `translateY(calc(-50% + ${item.contentOffsetY}))`
          }
        }

        return style
      },
      goToContact() {
        this.$router.push({ path: '/', hash: '#contact-form' })
      },
      hasBannerLink(item) {
        return item && item.jumpType && item.jumpType !== 'NONE'
      },
      handleBannerClick(item) {
        if (this.hasBannerLink(item)) {
          this.handleBannerAction(item)
        }
      },
      handleBannerAction(item) {
        if (!item) return this.goToContact()
        const jumpType = item.jumpType
        const openInNewTab = item.openInNewTab
        const lang = this.$i18n.locale || 'en'

        switch (jumpType) {
          case 'EXTERNAL':
            if (item.jumpUrl) {
              window.open(item.jumpUrl, openInNewTab ? '_blank' : '_self')
            }
            break
          case 'PRODUCT':
            if (item.productId) {
              const route = `/${lang}/products/${item.productId}`
              if (openInNewTab) {
                window.open(route, '_blank')
              } else {
                this.$router.push(route)
              }
            }
            break
          case 'BLOG':
            if (item.blogId) {
              const route = `/${lang}/blog/${item.blogId}`
              if (openInNewTab) {
                window.open(route, '_blank')
              } else {
                this.$router.push(route)
              }
            }
            break
          case 'NOTICE':
            if (item.noticeId) {
              const route = `/${lang}/notices/${item.noticeId}`
              if (openInNewTab) {
                window.open(route, '_blank')
              } else {
                this.$router.push(route)
              }
            }
            break
          default:
            this.goToContact()
        }
      },
      actionsStyle(item) {
        const align = item.contentAlign
        if (align === 'center') return { justifyContent: 'center' }
        if (align === 'right') return { justifyContent: 'flex-end' }
        return {}
      },
    },
  }
</script>
<style lang="sass" scoped>
.noline
  text-decoration: none

.cursor-pointer
  cursor: pointer

// 确保背景图片填满容器，避免大屏幕留白
.v-img
  background-size: cover !important
  background-repeat: no-repeat !important
  background-position: center center !important
  position: relative

.hero-content
  position: absolute
  left: 10vw
  top: 50%
  transform: translateY(-50%)
  max-width: 1200px

.hero-title
  text-align: left
  font-size: 2.5vw
  color: #1f2a44
  font-weight: 600
  line-height: 1.2

.hero-subtitle
  text-align: left
  font-size: 1.4vw
  color: #57627a
  font-weight: 400
  line-height: 1.3
  margin-top: 12px

.hero-actions
  margin-top: 18px
  display: flex

@media screen and (max-width: 960px)
  .hero-content
    left: 6vw
    top: 50%
    max-width: 90%
  .hero-title
    font-size: 5vw
  .hero-subtitle
    font-size: 3vw
</style>
